import React, { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeftIcon, ClockIcon, PenLineIcon } from 'lucide-react';
import { Reveal } from '../components/ui/Reveal';
import { CtaBand } from '../components/page/CtaBand';
import { RESOURCE_GUIDES } from '../components/resourcesData';
import { GUIDE_CONTENT, GUIDE_SECTIONS } from '../components/guides/guideContent';
import { GuideToc } from '../components/guides/GuideToc';
function updateMetaTag(attribute: 'name' | 'property', key: string, content: string) {
  const selector = `meta[${attribute}="${key}"]`;
  const existingTag = document.head.querySelector<HTMLMetaElement>(selector);
  const tag = existingTag ?? document.createElement('meta');
  const previousContent = existingTag?.getAttribute('content') ?? null;
  if (!existingTag) {
    tag.setAttribute(attribute, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
  return () => {
    if (!existingTag) {
      tag.remove();
      return;
    }
    if (previousContent === null) {
      existingTag.removeAttribute('content');
    } else {
      existingTag.setAttribute('content', previousContent);
    }
  };
}
export function GuideDetail() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const guide = RESOURCE_GUIDES.find(entry => entry.slug === slug);
  const GuideBody = slug ? GUIDE_CONTENT[slug] : undefined;
  const sections = (slug ? GUIDE_SECTIONS[slug] : undefined) ?? [];
  const relatedGuides = RESOURCE_GUIDES.filter(entry => entry.slug !== slug).slice(0, 3);
  useEffect(() => {
    if (!guide) return;
    const previousTitle = document.title;
    document.title = `${guide.title} | Working With Talent Agencies | Zano`;
    const restoreDescription = updateMetaTag('name', 'description', guide.summary);
    const restoreOgTitle = updateMetaTag('property', 'og:title', guide.title);
    const restoreOgDescription = updateMetaTag('property', 'og:description', guide.summary);
    return () => {
      document.title = previousTitle;
      restoreDescription();
      restoreOgTitle();
      restoreOgDescription();
    };
  }, [guide]);
  if (!guide) {
    return <main className="mx-auto max-w-3xl px-4 pb-24 pt-40 text-center sm:px-6 lg:px-8" data-id="element-2608" data-mp="31prkm6lsvwc0">
        <h1 className="text-3xl font-bold tracking-tight text-zano-charcoal" data-id="element-2609" data-mp="36r76g0vsy6vg">Guide not found</h1>
        <p className="mt-3 text-base text-gray-600" data-id="element-2610" data-mp="3aqzfeep3yf1c">This guide may have moved or is not published yet.</p>
        <Link to="/resources" className="mt-7 inline-flex items-center gap-1.5 text-sm font-bold text-zano-pink" data-id="element-2611" data-mp="2e3l5z1hvfict">
          <ArrowLeftIcon size={16} aria-hidden="true" data-id="element-2612" data-mp="2iz0f5g4xoq2w" /> Back to resources
        </Link>
      </main>;
  }
  return <>
      <article data-id="element-2613" data-mp="2rt6f7k5vcj7n">
        <header className="relative overflow-hidden pb-12 pt-32 lg:pt-40" data-id="element-2614" data-mp="2rty82n8302u8">
          <div className="pointer-events-none absolute left-1/2 top-0 -z-10 h-[360px] w-[820px] -translate-x-1/2 rounded-full bg-zano-lavender/40 blur-3xl" data-id="element-2615" data-mp="1vbiib66tb1t2" />
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8" data-id="element-2616" data-mp="1zbknvqfo4fdm">
            <Link to="/resources" className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-500 transition-colors hover:text-zano-pink" data-id="element-2617" data-mp="1rtuvjq0n6vre">
              <ArrowLeftIcon size={16} aria-hidden="true" data-id="element-2618" data-mp="1msozbi7rwqd4" /> Resources
            </Link>
            <div className="mt-6 flex flex-wrap items-center gap-2" data-id="element-2619" data-mp="1iv11o7ep4jhj">
              {guide.topics.map(topic => <span key={topic} className="rounded-full border border-zano-pink/25 bg-zano-blush px-2.5 py-1 text-[11px] font-semibold text-zano-pink" data-id="element-2620" data-mp="1dw6bulib9unx">{topic}</span>)}
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-500" data-id="element-2621" data-mp="141xhds9pzrn4"><ClockIcon size={13} aria-hidden="true" data-id="element-2622" data-mp="z5gs2uqsb78g" />{guide.readingTime}</span>
            </div>
            <h1 className="mt-5 max-w-3xl text-3xl font-bold leading-[1.1] tracking-tight text-zano-charcoal sm:text-4xl lg:text-5xl" data-id="element-2623" data-mp="v4wzm4h5orsn">{guide.title}</h1>
            <p className="mt-5 max-w-3xl text-lg leading-relaxed text-gray-600" data-id="element-2624" data-mp="l4mjoc3ql69s">{guide.summary}</p>
          </div>
        </header>

        <div className="mx-auto max-w-6xl px-4 pb-16 sm:px-6 lg:px-8" data-id="element-2625" data-mp="2l8qhpmx8ph11">
          <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_280px] lg:gap-12" data-id="element-2626" data-mp="2q70axrzem7k0">
            <div className="min-w-0 max-w-3xl lg:order-1" data-id="element-2627" data-mp="2bhvt4bwiu5cf">
              {GuideBody ? <GuideBody data-id="element-2628" data-mp="2ggp7e6jmu0dn" /> : <Reveal data-id="element-2629" data-mp="2gfe407u3mun2">
                  <div className="rounded-2xl border border-dashed border-gray-300 bg-gray-50/70 p-8 text-center" data-id="element-2630" data-mp="2gem8ex0i4wws">
                    <PenLineIcon size={24} className="mx-auto text-zano-pink" aria-hidden="true" data-id="element-2631" data-mp="22jac4l6p6ydr" />
                    <h2 className="mt-4 text-lg font-bold tracking-tight text-zano-charcoal" data-id="element-2632" data-mp="26g6jn0oy5zqg">Full guide coming soon</h2>
                    <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-gray-600" data-id="element-2633" data-mp="1xhl5dhqid6vh">This page is ready for the guide content. Once the copy is added, it will appear here with headings, sections, and takeaways.</p>
                  </div>
                </Reveal>}
            </div>

            {sections.length > 0 && <aside className="lg:order-2" data-id="element-2634" data-mp="1g90kjv7n7rfd">
                <GuideToc sections={sections} data-id="element-2635" data-mp="1b9o01h48uwfy" />
              </aside>}
          </div>
        </div>
      </article>

      {relatedGuides.length > 0 && <section className="border-t border-gray-100 bg-gray-50/60 py-16" aria-labelledby="related-guides-title" data-id="element-2636" data-mp="1pzmwnfg5hztb">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" data-id="element-2637" data-mp="1l5r1ekr9ur1u">
            <h2 id="related-guides-title" className="text-xl font-bold tracking-tight text-zano-charcoal sm:text-2xl" data-id="element-2638" data-mp="1yxfb6he1t3fn">Keep reading</h2>
            <div className="mt-6 grid gap-5 md:grid-cols-3" data-id="element-2639" data-mp="1u29v3z7buizh">
              {relatedGuides.map((related, index) => <Reveal key={related.slug} delay={index * 0.07} className="h-full" data-id="element-2640" data-mp="23ycvp0mj69kb" data-mpi={index}>
                  <Link to={`/resources/${related.slug}`} className="group flex h-full flex-col rounded-2xl border border-gray-200 bg-white p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-zano-pink/40 hover:shadow-float" data-id="element-2641" data-mp="ej5bibi07jlz">
                    <h3 className="text-base font-bold leading-snug tracking-tight text-zano-charcoal transition-colors group-hover:text-zano-pink" data-id="element-2642" data-mp="139jatrv7payx">{related.title}</h3>
                    <p className="mt-2 flex-1 text-sm leading-relaxed text-gray-600" data-id="element-2643" data-mp="18ayzdtwllzm2">{related.summary}</p>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-medium text-gray-500" data-id="element-2644" data-mp="1d5mcyxq2d9lv"><ClockIcon size={13} aria-hidden="true" data-id="element-2645" data-mp="1i4zb4emdy33r" />{related.readingTime}</span>
                  </Link>
                </Reveal>)}
            </div>
          </div>
        </section>}

      <CtaBand title="Ready to work with talent agencies?" subtitle="Search live rosters, discover represented creators, and reach the right talent managers directly." primaryCta={{
      label: 'Start Free Trial',
      to: '/pricing'
    }} data-id="element-2646" data-mp="1r1q3afxtgxt2" />
    </>;
}